import { localizedStrings } from '@core/strings'
import React, { ReactNode, useState } from 'react'
import { DialogTitle, IconButton, ButtonGroup } from '@mui/material'
import { useModal } from '@common/GlobalModal'
import PspdfkitComponent from 'components/modal/pdf/PspdfkitComponent'
import ClearIcon from '@mui/icons-material/Clear'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DownloadIcon from '@mui/icons-material/Download'
import { Box, Flex, Text } from '@fundamentals'
import { Button, DropdownButton } from '@common/material'
import { ModalDialogContainer } from '@common/modal/ModalDialogContainer'
import CarouselNavigation from '@common/CarouselNavigation'

interface PdfModal {
  title: string
  previewUrl: string
  documents?: Array<{
    uri?: string
    downloadUrl?: string
    getUrl?: string
    id?: number
    siteBoxDocumentVersionId?: number
  }>
  footerActions?: ReactNode
  canDownload?: boolean
  readOnly?: boolean
  onAnnotate?: (blob: Blob, url: string) => void
  onDownloadAll?: () => void
}

export const PdfModal: React.FC<PdfModal> = ({
  title,
  previewUrl,
  documents,
  footerActions,
  canDownload,
  readOnly,
  onAnnotate,
  onDownloadAll,
}) => {
  const { hideModal } = useModal()

  const [annotations, setAnnotations] = useState({ blob: null, url: null })

  // Use documents array if provided, otherwise fall back to single previewUrl
  const documentArray = documents || [{ uri: previewUrl }]
  const hasMultipleDocuments = documentArray.length > 1

  // Find the index of the previewUrl in the documents array
  const initialDocumentIndex = documentArray.findIndex(
    (document) =>
      document.uri === previewUrl ||
      document.getUrl === previewUrl ||
      document.downloadUrl === previewUrl,
  )

  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(
    initialDocumentIndex >= 0 ? initialDocumentIndex : 0,
  )
  const currentDocument = documentArray[currentDocumentIndex]
  const currentDocumentUrl =
    currentDocument?.uri ||
    currentDocument?.downloadUrl ||
    currentDocument?.getUrl

  const handlePreviousDocument = () => {
    setCurrentDocumentIndex((prev) => {
      const newIndex = prev === 0 ? documentArray.length - 1 : prev - 1
      return newIndex
    })
  }

  const handleNextDocument = () => {
    setCurrentDocumentIndex((prev) => {
      const newIndex = prev === documentArray.length - 1 ? 0 : prev + 1
      return newIndex
    })
  }

  return (
    <ModalDialogContainer maxWidth='lg'>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        <Flex alignItems='center'>
          <Box>{title}</Box>
          {hasMultipleDocuments && (
            <Flex ml={2} alignItems='center'>
              <Text fontSize='14px' color='grey.600'>
                {currentDocumentIndex + 1} of {documentArray.length}
              </Text>
            </Flex>
          )}
        </Flex>
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <Box width='100%' height='80vh' mb={1.5} position='relative'>
        {currentDocumentUrl && (
          <Flex width='100%' height='100%'>
            <PspdfkitComponent
              document={currentDocumentUrl}
              readOnly={readOnly}
              onAnnotate={(blob, url) => setAnnotations({ blob, url })}
            />
          </Flex>
        )}

        {hasMultipleDocuments && readOnly && (
          <CarouselNavigation
            onPrevious={handlePreviousDocument}
            onNext={handleNextDocument}
            px={3}
          />
        )}
      </Box>

      {(!readOnly || !!footerActions || canDownload) && (
        <Flex justifyContent='flex-start' sx={{ pb: 2, px: 2, pt: 0, gap: 1 }}>
          {footerActions}
          {canDownload &&
            !!currentDocumentUrl &&
            !currentDocument?.siteBoxDocumentVersionId && (
              <ButtonGroup variant='contained'>
                <Button
                  component='a'
                  href={currentDocumentUrl}
                  sx={{ textDecoration: 'none' }}
                >
                  {localizedStrings.download}
                </Button>
                {!!onDownloadAll && (
                  <DropdownButton
                    label=''
                    options={[
                      {
                        title: localizedStrings.downloadAll,
                        onSelect: () => onDownloadAll(),
                        icon: <DownloadIcon fontSize='small' />,
                      },
                    ]}
                    renderButton={(onClick) => {
                      return (
                        <Button size='small' sx={{ flex: 1 }} onClick={onClick}>
                          <ArrowDropDownIcon fontSize='small' />
                        </Button>
                      )
                    }}
                  />
                )}
              </ButtonGroup>
            )}
          {!readOnly && (
            <Button
              variant='contained'
              onClick={() => onAnnotate(annotations.blob, annotations.url)}
              disabled={!annotations.url}
            >
              {localizedStrings.saveAnnotations}
            </Button>
          )}
        </Flex>
      )}
    </ModalDialogContainer>
  )
}
